/* UGC video lightbox: tapping a tile opens a reels-style modal that plays
   the clip large with sound. One shared <video> gets its source swapped on
   navigation — iOS blesses gesture activation per media element, so after
   the opening tap later swaps may keep playing unmuted. Dawn's ModalDialog
   is deliberately not used: its pauseAllMedia() would freeze the muted
   autoplaying tiles with no way back. */
if (!customElements.get('wristx-ugc-lightbox')) {
  customElements.define(
    'wristx-ugc-lightbox',
    class WristxUgcLightbox extends HTMLElement {
      connectedCallback() {
        // reattaching (e.g. theme-editor reorder) re-fires this on the same node
        if (this.wxlbInitialized) return;
        this.wxlbInitialized = true;

        this.items = Array.from(this.querySelectorAll('.wxugc__tile')).map(function (tile) {
          var video = tile.querySelector('video');
          var source = video && video.querySelector('source');
          var src = source ? source.getAttribute('src') : '';
          return {
            tile: tile,
            video: video,
            // the tile runs ReelUp's trimmed muted teaser; the lightbox wants
            // the full-length clip with audio when the template provides it
            src: tile.getAttribute('data-wxlb-full') || src,
            poster: video ? video.getAttribute('poster') || '' : ''
          };
        }).filter(function (item) {
          return item.video && item.src;
        });
        if (!this.items.length) return;

        this.modal = this.querySelector('[data-wxlb-modal]');
        this.frame = this.querySelector('[data-wxlb-frame]');
        this.player = this.querySelector('[data-wxlb-video]');
        this.playerSource = this.querySelector('[data-wxlb-source]');
        this.dotsWrap = this.querySelector('[data-wxlb-dots]');
        this.countEl = this.querySelector('[data-wxlb-count]');
        this.closeBtn = this.querySelector('[data-wxlb-close-btn]');
        this.stateEl = this.querySelector('[data-wxlb-state]');
        this.progressEl = this.querySelector('[data-wxlb-progress]');
        this.muteBtn = this.querySelector('[data-wxlb-mute]');
        if (!this.modal || !this.player || !this.playerSource) return;

        this.currentIndex = 0;
        this.opener = null;
        this.lastSwipeAt = 0;
        this.onKeydown = this.onKeydown.bind(this);

        var self = this;

        this.items.forEach(function (item, index) {
          var btn = item.tile.querySelector('[data-wxlb-open]');
          if (btn) {
            btn.addEventListener('click', function () {
              self.open(index, btn);
            });
          }
        });

        this.querySelectorAll('[data-wxlb-close], [data-wxlb-close-btn]').forEach(function (el) {
          el.addEventListener('click', function () {
            self.close();
          });
        });

        var prev = this.querySelector('[data-wxlb-prev]');
        var next = this.querySelector('[data-wxlb-next]');
        if (prev) prev.addEventListener('click', function () { self.step(-1); });
        if (next) next.addEventListener('click', function () { self.step(1); });

        if (this.muteBtn) {
          this.muteBtn.addEventListener('click', function () {
            self.setMuted(!self.player.muted);
          });
        }

        // the video has no native controls: tapping it toggles playback.
        // A swipe gesture may or may not synthesize a click afterwards, so a
        // one-shot flag would go stale — use a short time window instead.
        this.frame.addEventListener('click', function (e) {
          if (e.target.closest('button')) return; // pills live inside the frame
          if (Date.now() - self.lastSwipeAt < 500) return;
          self.togglePlay();
        });

        // the paused overlay is the play/pause feedback
        this.player.addEventListener('play', function () {
          if (self.stateEl) self.stateEl.hidden = true;
        });
        this.player.addEventListener('pause', function () {
          if (self.stateEl && !self.modal.hidden) self.stateEl.hidden = false;
        });
        this.player.addEventListener('timeupdate', function () {
          if (self.progressEl && self.player.duration > 0) {
            self.progressEl.style.transform =
              'scaleX(' + self.player.currentTime / self.player.duration + ')';
          }
        });

        this.buildDots();
        if (this.items.length < 2) {
          if (prev) prev.hidden = true;
          if (next) next.hidden = true;
          if (this.dotsWrap) this.dotsWrap.hidden = true;
          if (this.countEl) this.countEl.hidden = true;
        }

        this.bindSwipe();
      }

      disconnectedCallback() {
        // don't leave the page scroll-locked if the section is removed mid-view
        if (this.modal && !this.modal.hidden) this.close();
      }

      buildDots() {
        if (!this.dotsWrap) return;
        var self = this;
        this.dots = this.items.map(function (item, index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'wxugc-lb__dot';
          dot.setAttribute('aria-label', 'Video ' + (index + 1) + ' of ' + self.items.length);
          dot.addEventListener('click', function () {
            self.goTo(index);
          });
          self.dotsWrap.appendChild(dot);
          return dot;
        });
      }

      bindSwipe() {
        if (!this.frame) return;
        var self = this;
        var startX = 0;
        var startY = 0;
        var tracking = false;

        this.frame.addEventListener('touchstart', function (e) {
          var touch = e.changedTouches[0];
          tracking = true;
          startX = touch.clientX;
          startY = touch.clientY;
        }, { passive: true });

        this.frame.addEventListener('touchend', function (e) {
          if (!tracking) return;
          tracking = false;
          var touch = e.changedTouches[0];
          var dx = touch.clientX - startX;
          var dy = touch.clientY - startY;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            self.lastSwipeAt = Date.now(); // a synthesized click must not toggle playback
            self.step(dx < 0 ? 1 : -1);
          } else if (dy > 70 && Math.abs(dy) > Math.abs(dx)) {
            self.lastSwipeAt = Date.now();
            self.close();
          }
        }, { passive: true });
      }

      open(index, opener) {
        // Safari doesn't focus a clicked button, so activeElement alone
        // wouldn't get focus back to the tile on close
        this.opener = opener || document.activeElement;
        this.pauseTiles();
        this.setChatHidden(true);
        this.setMuted(false); // every fresh open starts with sound
        if (this.stateEl) this.stateEl.hidden = true;
        this.lastSwipeAt = 0;
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        if (window.trapFocus) window.trapFocus(this.modal, this.closeBtn);
        document.addEventListener('keydown', this.onKeydown);
        this.goTo(index);
      }

      close() {
        this.player.pause();
        this.playerSource.removeAttribute('src');
        this.player.removeAttribute('poster');
        this.player.load();
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);
        if (window.removeTrapFocus) window.removeTrapFocus(this.opener);
        this.setChatHidden(false);
        this.resumeTiles();
      }

      // keep the Intercom launcher from floating over the player
      setChatHidden(hidden) {
        document.body.classList.toggle('wxugc-lb-open', hidden);
        if (typeof window.Intercom === 'function') {
          try {
            window.Intercom('update', { hide_default_launcher: hidden });
          } catch (e) { /* widget not booted */ }
        }
      }

      step(delta) {
        this.goTo(this.currentIndex + delta);
      }

      goTo(index) {
        var count = this.items.length;
        index = ((index % count) + count) % count;
        this.currentIndex = index;

        var item = this.items[index];
        this.playerSource.src = item.src;
        if (item.poster) {
          this.player.setAttribute('poster', item.poster);
        } else {
          this.player.removeAttribute('poster');
        }
        if (this.progressEl) this.progressEl.style.transform = 'scaleX(0)';
        this.player.load();
        var self = this;
        this.player.play().catch(function () {
          // Low Power Mode etc. — surface the tap-to-play overlay
          if (self.stateEl) self.stateEl.hidden = false;
        });

        if (this.dots) {
          this.dots.forEach(function (dot, i) {
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
        }
        if (this.countEl) this.countEl.textContent = (index + 1) + ' / ' + count;
      }

      onKeydown(e) {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.step(1);
        else if (e.key === 'ArrowLeft') this.step(-1);
        else if (e.key === ' ' && e.target === document.body) {
          e.preventDefault(); // keep Space from scrolling the page
          this.togglePlay();
        }
      }

      togglePlay() {
        var self = this;
        if (this.player.paused) {
          this.player.play().catch(function () {
            if (self.stateEl) self.stateEl.hidden = false;
          });
        } else {
          this.player.pause();
        }
      }

      setMuted(muted) {
        this.player.muted = muted;
        if (!this.muteBtn) return;
        this.muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
        var on = this.muteBtn.querySelector('.wxugc-lb__mute-on');
        var off = this.muteBtn.querySelector('.wxugc-lb__mute-off');
        var label = this.muteBtn.querySelector('[data-wxlb-mute-label]');
        // toggleAttribute, not .hidden — SVG elements lack the hidden property
        if (on) on.toggleAttribute('hidden', muted);
        if (off) off.toggleAttribute('hidden', !muted);
        if (label) label.textContent = muted ? 'Sound off' : 'Sound on';
      }

      pauseTiles() {
        this.items.forEach(function (item) {
          item.video.pause();
        });
      }

      // legal without a gesture because the tiles are muted
      resumeTiles() {
        this.items.forEach(function (item) {
          item.video.play().catch(function () { /* autoplay may be blocked */ });
        });
      }
    }
  );
}
